import { Fragment, useEffect, useState } from 'react';
import { PlayerForSaveDto } from '@/api/models';
// import Checkbox from '@/common/Checkbox';
import ContractEdit from './Edit';
import ContractHistory from './PlayerContractHistory';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { getContractStatus, ContractStatusColor } from '@/library/ContractSettingsHelper';
import { getPlayerContractHistory } from '@/api/endpoints/getPlayerContractHistory';
import { Contract, ContractHistoryDiff, ContractItems, CurrentContract } from '@/api/models/PlayerContractHistory';
import EditPencilIcon from '@/assets/icons/ep_edit.svg?react';
import HistoryIcon from '@/assets/icons/history.svg?react';
import styles from './PlayerContract.module.scss';

interface IPlayerContractProps {
  showModal: boolean;
  player: PlayerForSaveDto | null;
  isMyPlayer?: boolean;
  /* eslint-disable no-unused-vars */
  onSave: (contract: Contract) => void;
  handleMyPlayer?: (add: boolean) => Promise<void>;
  /* eslint-enable no-unused-vars */
  enableEdit: boolean;
  enableHistory: boolean;
}

function PlayerContract({ player, onSave, enableEdit, enableHistory }: IPlayerContractProps) {
  const isOnline = useOnlineStatus();
  const [showEdit, setShowEdit] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [contract, setContract] = useState({ status: 'no contract' } as CurrentContract);
  const [contractHistoryDiff, setContractHistoryDiff] = useState([] as ContractHistoryDiff[]);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const refreshContractData = async () => {
      setShowDetails(false); //hide form till contract data is loaded
      //if this is an admin, always get the latest contract data using the history api endpoint
      //but if this is not and admin, then we need to use the current player even though the data could be out of date
      if (enableEdit && isOnline) {
        let newContractHistory = await getPlayerContractHistory(player?.playerId || 0);
        if (newContractHistory.currentContract) {
          setContract(newContractHistory.currentContract);
        }
      } else {
        if (player && player.contract) {
          setContract(player.contract);
        }
      }
      setShowDetails(true);
    };
    refreshContractData();
  }, [player, enableEdit]);

  const getHistory = async () => {
    //only allowed to get history data for contract admins
    if (enableEdit) {
      let newContractHistory = await getPlayerContractHistory(player?.playerId || 0);
      let newDiff = {} as ContractHistoryDiff;
      let newContractHistoryDiff = [] as ContractHistoryDiff[];

      //save current contract if it exists
      if (newContractHistory.currentContract && newContractHistory.currentContract.startDate) {
        //sort history by descending order
        newContractHistory.contractsHistory.sort((a, b) => b.snapshotId - a.snapshotId);
        //insert current contract into front of history list so that everything can be processed in one loop
        newContractHistory.contractsHistory.unshift({
          ...newContractHistory.currentContract,
          snapshotId: 0,
          snapshotCreatedAt: newContractHistory.currentContract.modifyDate.toString(),
        });
        let numberContracts = newContractHistory.contractsHistory.length;
        for (let ix = 0; ix <= numberContracts - 1; ix++) {
          newDiff = {
            fullName: newContractHistory.contractsHistory[ix].modifiedByUser.fullName,
            snapshotCreatedAt: newContractHistory.contractsHistory[ix].modifyDate.toString(),
            startDate: newContractHistory.contractsHistory[ix].startDate,
            endDate: newContractHistory.contractsHistory[ix].endDate,
            itemsAdded: Array<string>(),
            itemsRemoved: Array<string>(),
          };
          // Note: don't do a diff for last item in array */
          if (ix !== numberContracts - 1) {
            newContractHistory.contractsHistory[ix].items.forEach((item) => {
              //which items were added?
              if (newContractHistory.contractsHistory[ix + 1].items.indexOf(item) == -1) {
                newDiff.itemsAdded.push(item);
              }
            });
            //which items were removed?
            newContractHistory.contractsHistory[ix + 1].items.forEach((item) => {
              //which items were added?
              if (newContractHistory.contractsHistory[ix].items.indexOf(item) == -1) {
                newDiff.itemsRemoved.push(item);
              }
            });
          } else {
            //for the first contract in the history, consider every item to be added
            newDiff.itemsAdded = newContractHistory.contractsHistory[ix].items;
          }
          newContractHistoryDiff.push(newDiff);
        }
      }
      setContractHistoryDiff(newContractHistoryDiff);
    }
    setShowDetails(true);
  };

  const handleToggleEdit = () => {
    setShowEdit(true);
  };

  const itemIsInContract = (item: string) => {
    return contract.items && contract.items.indexOf(item) > -1;
  };

  const handleShowHistory = async () => {
    await getHistory();
    setShowHistory(true);
  };

  const handleCloseHistory = () => {
    setShowHistory(false);
  };

  const handleCancelEditForm = () => {
    setShowEdit(false);
  };

  const handleCloseEditForm = async (newContract: CurrentContract) => {
    // let status = getContractStatusByDate(newContract.endDate);
    const thisContract = { ...contract, startDate: newContract.startDate, endDate: newContract.endDate, items: newContract.items, status: newContract.status } as CurrentContract;
    setContract(thisContract);
    await getHistory();
    setShowEdit(false);
  };

  //formatting the date moves the day to yesterday when entering manually but not when reading from the database. Make the output consistent
  const formatDate = (date: string) => {
    if (date.indexOf('T') == -1) {
      date = (date as any).replaceAll('-', '/'); //this prevents js from using GMT to change the date to yesterday
    }
    let newDate = new Date(date);
    date = newDate.toLocaleDateString();
    return date;
  };

  return (
    <>
      {!showEdit && !showHistory && (
        <>
          <div className={styles.navbar}>
            <div className={styles.playerName}>{player?.fullName || ''}</div>
          </div>
          <span className={styles.subheading}>
            <span className={styles.tourName}>{player?.defaultTourName || ''}</span> <span className={styles.playerId}>ID - {player?.playerId || 0}</span>
          </span>
          <div className={`${styles.subheading} ${styles.contractSection}`}>
            {showDetails && (
              <>
                <div className={styles.bold}>Contract</div>
                <div className={`${styles.status} ${styles[ContractStatusColor(contract)]}`}>{getContractStatus(contract)}</div>
                {enableEdit && isOnline && (
                  <>
                    <button type="button" className={styles.iconButton} aria-label="Edit contract" onClick={handleToggleEdit}>
                      <EditPencilIcon aria-hidden="true" className={styles.svgButton} />
                    </button>
                    {enableHistory && (
                      <button type="button" className={styles.iconButton} aria-label="View contract history" onClick={handleShowHistory}>
                        <HistoryIcon aria-hidden="true" className={styles.svgButton} />
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {showDetails && enableEdit && contract.endDate && <div className={styles.expirationDate}>Exp. {formatDate(contract.endDate)}</div>}
          {/* If a user is not admin and the contract is expired, don't show items. If admin then always show items so they can make changes */}
          {(enableEdit || getContractStatus(contract) == 'active') && (
            <div className={styles.items}>
              {ContractItems.map((item, ix) => (
                <Fragment key={ix}>
                  {itemIsInContract(item) && (
                    <div className={styles.item} key={ix}>
                      {item}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          )}
          {/* {!enableEdit && handleMyPlayer && (
            <div className={styles.mobileFooter}>
              <hr />
              <div>
                <Checkbox label="My Player" checked={isMyPlayer || false} onClick={handleMyPlayer} />
              </div>
            </div>
          )} */}
        </>
      )}
      {showEdit && <ContractEdit player={player} handleCancelButton={handleCancelEditForm} contract={contract} handleOnSave={onSave} handleCloseButton={handleCloseEditForm} />}
      {showHistory && <ContractHistory player={player} contractHistoryDiff={contractHistoryDiff} handleCloseHistory={handleCloseHistory} />}
    </>
  );
}

export default PlayerContract;
